import { skipToken } from '@apollo/client/react'
import { compact, noop } from 'lodash-es'
import type {
  HTMLAttributes,
  MouseEvent,
  PropsWithChildren,
  ReactNode,
} from 'react'
import { createContext, useCallback, useContext, useState } from 'react'
import { Button as AriaButton } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'react-toastify'
import { isMatching, match, P } from 'ts-pattern'
import MingcuteChat1Fill from '~icons/mingcute/chat-1-fill'
import MingcuteCheckFill from '~icons/mingcute/check-fill'
import MingcuteFireFill from '~icons/mingcute/fire-fill'
import MingcuteLockFill from '~icons/mingcute/lock-fill'
import MingcuteMore2Fill from '~icons/mingcute/more-2-fill'
import MediaPreview from 'src/components/Common/MediaPreview'
import ExpandableBox from 'src/components/ExpandableBox'
import type { ModelTypeTagColors } from 'src/components/GenerationModel/modelTypeDisplay'
import { useGenerationModelColorConfig } from 'src/components/GenerationModel/modelTypeDisplay'
import BaseLikeButton from 'src/components/LikeButton/BaseLikeButton'
import { LikeHeart } from 'src/components/LikeButton/LikeHeart'
import { dialogReport } from 'src/components/Report/ReportDialog'
import { UIMenu, UIMenuItem, UIMenuTrigger } from 'src/components/ui/Menu'
import Text from 'src/components/ui/Text'
import { useMyInfo } from 'src/services/apollo/auth'
import type {
  GenerationModelPreviewFragment,
  GenerationModelPreviewWithTrainingTasksFragment,
} from 'src/services/apollo/generated/graphql'
import {
  GenerationModelMarkType,
  RefType,
} from 'src/services/apollo/generated/graphql'
import {
  executeMarkGenerationModel,
  useGetGenerationModel,
} from 'src/services/apollo/generation-model'
import { deleteModel } from 'src/services/helpers/model'
import { cx } from 'src/utils/cx'
import dayjs from 'src/utils/dayjs'
import { formatNum } from 'src/utils/formatNum'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'
import {
  createModelUrl,
  isLora,
  isLoraCategoryNotSet,
  isUserLora,
} from 'src/utils/model'
import { track } from 'src/utils/tracker'
import { isValidUrl } from 'src/utils/url'
import { isModelPrivate } from '~/utils/generationModel/modelPermissions'
import { openSignModal } from '../Account/utils'
import { CardCornerDebugInfo } from '../Admin/UserContentInfo/CardCornerDebugInfo'
import { dialogEditModel } from './GenerationModelEdit/ModelEditDialog'
import { getIncompleteTrainingTasksFromModel } from './GenerationModelTrainingTask/trainingTask'
import { publishLora } from './helpers'
import {
  getModelLoraBaseModelChipsConfig,
  getModelPreviewEAChipConfig,
  getModelTypeChipConfig,
  TrainingTaskStatus,
} from './ModelTypeChip'

type GenerationModelForModelCard =
  | GenerationModelPreviewWithTrainingTasksFragment
  | GenerationModelPreviewFragment

export type ModelCardProps = ModelCardCommonProps &
  Partial<{
    /** The ID of the model to display */
    modelId: string
    /** The model data to display */
    model: GenerationModelForModelCard
    /** If `model` is provided, this function should be used to refresh the model list in the parent */
    mutateModels: () => any
  }>

type ModelCardCommonProps = PropsWithChildren &
  StyleProps & {
    /**
     * The path for this card to link to.
     *
     * Pass `true` to use the model page address
     */
    link?: string | boolean
    /**
     * Whether the card is selected
     *
     * Pass `undefined` to exit selection mode.
     */
    selected?: boolean
    /**
     * The props of the card menu items
     */
    menu?: Array<ModelCardMenuItemProps | Falsy>
    scene?: string
    onClick?: (ev: MouseEvent, model: GenerationModelForModelCard) => void
    /**
     * Callback triggered when some updating action (e.g., edit model info) has been invoked
     */
    onUpdate?: () => void
    slots?: {
      typeLabel?:
        | ReactNode
        | ((props: {
            model: GenerationModelForModelCard
            typeLabel: ReactNode
          }) => ReactNode)
    }
  }

type ModelCardMenuItemProps = StyleProps & {
  key: string
  label: ReactNode
  onClick?: (model: GenerationModelForModelCard) => void
}

type ModelCardState = {
  loading: boolean
  selecting: boolean
  trainingInProgress: boolean
  isMyModel: boolean
  isPrivate: boolean
  isUserLora: boolean
}

const ModelCardContext = createContext(
  {} as {
    model: GenerationModelForModelCard | undefined
    state: ModelCardState
    action: { mutate?: () => any }
  },
)

/**
 * A model card, mostly used in model list
 */
// oxfmt-ignore
export default function ModelCard (props: ModelCardCommonProps & Pick<ModelCardProps, 'modelId'>): JSX.Element
// oxfmt-ignore
export default function ModelCard (props: ModelCardCommonProps & Pick<ModelCardProps, 'model' | 'mutateModels'>): JSX.Element
// oxfmt-ignore
export default function ModelCard (props: ModelCardProps): JSX.Element {
  const modelIdToFetch = props.model ? undefined : props.modelId
  const {
    data: model = props.model,
    loading,
    refetch: refetchModel,
  } = useGetGenerationModel(modelIdToFetch || skipToken)
  const { data: me } = useMyInfo()

  const incompleteTrainingTasks = getIncompleteTrainingTasksFromModel(model)

  const state: ModelCardState = {
    loading,
    selecting: typeof props.selected === 'boolean',
    trainingInProgress: isUserLora(model?.type) && incompleteTrainingTasks.length > 0,
    isMyModel: isMatching({ authorId: P.intersection(P.string, me?.id) }, model),
    isPrivate: isModelPrivate(model),
    isUserLora: isUserLora(model?.type),
  }

  /**
   * Avoid refetching when version id doesn't exist, e.g. in model dialog (which doesn't have version from props)
   * Should be revisited after updating to v4.0.11
   * @see https://github.com/apollographql/apollo-client/issues/12996
   */
  const mutateModel = useCallback(() => {
    if (!modelIdToFetch) return
    return refetchModel()
  }, [modelIdToFetch])

  /* Mutate parent model list if model data comes from parent */
  const action = { mutate: props.model ? props.mutateModels : mutateModel }

  return (
    <ModelCardContext.Provider value={{ model, state, action }}>
      <ModelCardActual {...props} />
    </ModelCardContext.Provider>
  )
}

/**
 * The actual implementation of the model card
 */
function ModelCardActual(props: ModelCardCommonProps) {
  const { model, state } = useContext(ModelCardContext)
  const { t } = useTranslation(['market'])
  const isMobile = useIsMobile()
  const colorConfig = useGenerationModelColorConfig()

  const [mouseHovered, setMouseHovered] = useState(false)

  /*
   * Render
   */

  if (!model)
    return (
      <ModelCardRoot
        className={cx(
          'aspect-square bg-zinc-200 dark:bg-zinc-700 rounded-[20px]',
          state.loading && 'animate-pulse',
          props.className,
        )}
        style={props.style}
      />
    )

  const overlayType = match({ ...props, model })
    .with(
      { model: { type: P.when(isUserLora) } },
      () => state.isMyModel && state.trainingInProgress,
      () => 'training-progress',
    )
    .with(
      { model: P.when(isLoraCategoryNotSet) },
      () => state.isMyModel,
      () => 'category-not-set',
    )
    .with(
      { model: { latestAvailableVersion: P.nullish } },
      () => 'no-available-versions',
    )
    .with({ selected: false }, () => 'unselected')
    .otherwise(() => null)

  return (
    <ModelCardRoot
      id={`model-card-${model.id}`}
      link={props.link}
      className={cx(
        '[--extent:5px]',
        'group/model-card',
        'relative isolate aspect-square rounded-[20px] shadow-lg focus:outline outline-[3px] outline-offset-2 outline-zinc-900 dark:outline-zinc-300',
        props.className,
      )}
      style={props.style}
      onMouseEnter={() => setMouseHovered(true)}
      onMouseLeave={() => setMouseHovered(false)}
      onClick={ev => {
        props.onClick?.(ev, model)
      }}
    >
      <ExpandableBox
        data-expanded={mouseHovered ? '' : null}
        className="relative size-full ring-1 ring-black/10 dark:ring-white/10 rounded-[20px] data-[expanded]:rounded-[calc(20px+var(--extent))]"
      >
        {/* Cover image */}
        <MediaPreview
          mediaId={model?.mediaId}
          media={model?.media}
          thumbnail
          alt={[
            model.title?.trim(),
            `AI ${isLora(model.type) ? 'LoRA' : 'checkpoint'} model on PixAI`,
          ]
            .filter(Boolean)
            .join(' - ')}
          experiments={{ safetyBlur: model.flag?.shouldBlur }}
          className="size-full"
        />

        {/* Overlay */}
        <div
          className={cx(
            'absolute inset-0 pointer-events-none transition',
            match(overlayType)
              .with('unselected', () => 'bg-black/20')
              .with(P.string, () => 'bg-black/50')
              // TODO: `40px` is arbitrarily chosen, could be a more reasonable value
              .otherwise(() =>
                cx(
                  'bg-gradient-to-t from-black/50 from-[40px] to-35%',
                  mouseHovered && 'opacity-0',
                ),
              ),
          )}
        >
          {state.trainingInProgress && (
            <ModelCardLoraTrainingOverlay model={model} />
          )}
        </div>
      </ExpandableBox>

      {/* Type label */}
      {typeof props.slots?.typeLabel === 'function' ? (
        props.slots.typeLabel({
          model,
          typeLabel: (
            <ModelCardTypeLabel model={model} colorConfig={colorConfig} />
          ),
        })
      ) : (
        <ModelCardTypeLabel
          model={model}
          colorConfig={colorConfig}
          className="absolute top-2 left-2"
        />
      )}

      {/* Content */}
      <div className="absolute inset-0 flex flex-col justify-end w-full py-4 text-white text-shadow shadow-black">
        {match(overlayType)
          .with('category-not-set', () => (
            <LoraCategoryNotSetOverlay onUpdate={props.onUpdate} />
          ))
          .with('no-available-versions', () => (
            <ModelNoAvailableVersionsOverlay />
          ))
          .otherwise(() => null)}

        {/* Title */}
        <p className="px-4 line-clamp-3 font-medium">{model.title?.trim()}</p>

        {/* Stats */}
        {!state.isPrivate && !state.trainingInProgress && (
          <div className="mt-0.5 -mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 px-4 text-sm">
            <span className="inline-flex items-center gap-0.5">
              <MingcuteFireFill className="size-4 drop-shadow-[0_1px_2px_#000]" />
              {formatNum(model.refCount)}
            </span>
            <span className="inline-flex items-center gap-0.5">
              <MingcuteChat1Fill className="size-4 drop-shadow-[0_1px_2px_#000]" />
              {formatNum(model.commentCount)}
            </span>
            {isMobile ? null : (
              <ModelCardLikeButton model={model} scene={props.scene} />
            )}
          </div>
        )}

        {/* Private label */}
        {state.isPrivate && (
          <span className="self-start -mb-1.5 ml-2.5 mt-2 inline-flex items-center gap-0.5 h-5 px-1.5 text-black bg-white ring-1 ring-black/20 rounded-full">
            <MingcuteLockFill className="flex-none size-4" />
            <span className="text-xs font-medium uppercase text-shadow-none">
              {t('market:model.status.unpublished')}
            </span>
          </span>
        )}
      </div>

      {props.children}

      {/* Interaction area (top right) */}
      <div className="absolute top-0 right-0 overflow-visible size-0">
        {state.selecting ? (
          <div className="contents">
            <input
              type="checkbox"
              checked={props.selected}
              onChange={noop}
              className="peer absolute invisible"
              id={`model-card-${model.id}`}
            />
            <i className="absolute top-1.5 right-1.5 grid place-content-center size-7 text-transparent peer-checked:text-white bg-white/80 peer-checked:bg-purple-600 border-2 peer-checked:border-0 border-black/80 peer-checked:ring-1 ring-black/80 peer-checked:ring-black/10 rounded-full peer-checked:shadow-md">
              <MingcuteCheckFill className="size-5" />
            </i>
          </div>
        ) : (
          <>
            <CardCornerDebugInfo
              extra={model.extra}
              artworkSafetyScore={model.artworkSafetyScore}
            >
              {content => (
                <div className="absolute top-1.5 right-1.5 bg-black/50 py-[2px] px-2 w-max rounded-xl ring-1 ring-white/30 backdrop-blur">
                  {content}
                </div>
              )}
            </CardCornerDebugInfo>
          </>
        )}
      </div>
    </ModelCardRoot>
  )
}

function ModelCardRoot(
  props: HTMLAttributes<HTMLElement> & Pick<ModelCardCommonProps, 'link'>,
) {
  const { link, ...rootProps } = props
  const { model, state } = useContext(ModelCardContext)

  return match({ model, state, link })
    .with({ state: { selecting: true } }, () => (
      // Matches <input>'s id to prevent click event being triggered more than once
      <label {...rootProps} htmlFor={rootProps.id} />
    ))
    .with(
      { link: true, model: {} },
      { link: P.string.minLength(1) },
      ({ link, model }) => (
        <Link
          to={link === true ? createModelUrl(model) : link}
          {...rootProps}
        />
      ),
    )
    .otherwise(() => <div {...rootProps} />)
}

function ModelCardLoraTrainingOverlay(props: {
  model: GenerationModelForModelCard
}) {
  const { t } = useTranslation(['market'])

  const incompleteTrainingTasks = getIncompleteTrainingTasksFromModel(
    props.model,
  )
  return (
    <div className="absolute inset-0 flex flex-col justify-center items-center font-medium text-white text-shadow">
      {match(incompleteTrainingTasks[0])
        .with(
          {
            status: TrainingTaskStatus.running,
            extra: {
              progress: P.select('progress', P.number),
              estimatedTotalTime: P.select('estimatedTotalTime', P.number),
            },
          },
          ({ progress, estimatedTotalTime }) => (
            <>
              <span className="text-3xl">{progress}%</span>
              <span className="text-sm">
                {t('market:train-lora.model.estimated-time')}:{' '}
                {dayjs.duration(estimatedTotalTime, 'ms').humanize()}
              </span>
            </>
          ),
        )
        .with(
          { status: TrainingTaskStatus.waiting },
          { status: TrainingTaskStatus.running },
          () => (
            <span className="text-xl">
              {t('market:train-lora.model.waiting')}
            </span>
          ),
        )
        .with({ status: TrainingTaskStatus.failed }, () => (
          <span className="text-xl">{t('market:train-lora.model.failed')}</span>
        ))
        .otherwise(() => null)}
      {incompleteTrainingTasks.length > 1 && (
        <span className="text-xl">
          {t('market:train-lora.model.and-more-tasks', {
            count: incompleteTrainingTasks.length - 1,
          })}
        </span>
      )}
    </div>
  )
}

function ModelNoAvailableVersionsOverlay() {
  return (
    <div className="absolute inset-0 flex flex-col justify-center items-center gap-1 font-medium text-white text-shadow">
      <span className="text-xl">
        <Text i18nKey="market:model.versions-empty" />
      </span>
    </div>
  )
}
/**
 * Overlay for models with no category set
 */
function LoraCategoryNotSetOverlay(props: Pick<ModelCardProps, 'onUpdate'>) {
  const { model, state } = useContext(ModelCardContext)

  return (
    <div className="absolute inset-0 flex flex-col justify-center items-center gap-1 font-medium text-white text-shadow">
      <span className="text-xl">
        <Text i18nKey="market:submit.messages.await-info-update" />
      </span>
      {model && (
        <button
          className="h-8 px-2.5 text-sm text-black bg-white rounded-md"
          onClick={ev => {
            ev.preventDefault()
            ev.stopPropagation()
            dialogEditModel({
              model,
              mutate: () => {
                props.onUpdate?.()
              },
              highlightId: 'category-field',
            })
          }}
        >
          <Text i18nKey="action:update" />
        </button>
      )}
    </div>
  )
}

function ModelCardTypeLabel(
  props: StyleProps & {
    model: GenerationModelForModelCard
    colorConfig?: ModelTypeTagColors
  },
) {
  const { t } = useTranslation(['market'])
  const { colorConfig } = props

  const typeConfig =
    props.model.type &&
    getModelTypeChipConfig(
      t,
      {
        curations: props.model.curations,
        type: props.model.type,
      },
      colorConfig,
    )

  const previewEAConfig = getModelPreviewEAChipConfig(props.model)
  const loraBaseModelConfig =
    getModelLoraBaseModelChipsConfig(props.model, colorConfig) ?? []

  const modelTypeComp = typeConfig && (
    <span
      className={cx(
        'inline-flex items-center h-6 px-2 text-xs font-medium uppercase text-black rounded-[12px]',
        typeConfig.bgClassName,
      )}
      style={typeConfig.bgStyle}
    >
      {typeConfig.label}
    </span>
  )
  // Only display for Loras
  const loraBaseModelTypesComp = isLora(props.model.type) &&
    loraBaseModelConfig.length > 0 && (
      <span className="inline-flex items-center">
        {loraBaseModelConfig.map(config => (
          <span
            className={cx(
              'inline-flex items-center h-6 px-1 text-xs font-medium uppercase text-black first-of-type:rounded-l-[12px] first-of-type:pl-2 last-of-type:rounded-r-[12px] last-of-type:pr-2',
              config.bgClassName,
            )}
            key={config.label}
            style={config.bgStyle}
          >
            {config.label}
          </span>
        ))}
      </span>
    )
  const previewChipComp = previewEAConfig && (
    <span
      className={cx(
        'inline-flex items-center h-6 px-2 text-xs font-medium uppercase text-white rounded-[12px]',
        previewEAConfig.bgClassName,
      )}
    >
      {previewEAConfig.label}
    </span>
  )

  return (
    <div className={cx('flex items-center gap-1', props.className)}>
      {modelTypeComp}
      {loraBaseModelTypesComp}
      {previewChipComp}
    </div>
  )
}

// FIXME remove unused code
function ModelCardMenu(
  props: Pick<ModelCardProps, 'onUpdate'> & {
    menu?: Array<ModelCardMenuItemProps | Falsy>
  },
) {
  const { t } = useTranslation(['action', 'market', 'artwork'])
  const { model, state, action } = useContext(ModelCardContext)
  const { data: me } = useMyInfo()

  /*
   * Render
   */

  if (!model) return null

  const resolvedMenu: ModelCardMenuItemProps[] = compact(
    [
      /* Disable Download for guests */
      model.isDownloadable && {
        key: 'download',
        label: t('market:model.download'),
        onClick: () => {
          track('download_model_click', { type: model.type })

          if (!me) {
            // Refetch model after auth state changed to apply downloadUrl changes
            openSignModal(
              {
                onSuccess: () => action.mutate?.(),
              },
              { fromPath: 'download-model' },
            )
            return
          } else {
            const downloadUrl = model.latestAvailableVersion?.downloadUrl
            if (!isValidUrl(downloadUrl))
              return toast.error(t('market:model.notice.download-url-invalid'))
            const a = document.createElement('a')
            a.href = downloadUrl
            a.download = ''
            a.click()
            toast.info(t('market:model.notice.downloading'))
          }
        },
      },
      /* Report */
      // oxfmt-ignore
      me && !state.isMyModel && {
        key: 'report',
        label: t('artwork:action.report'),
        onClick: () => {
          track('report_click')
          dialogReport(RefType.Model, model.id)
        },
      },
      // oxfmt-ignore
      state.isMyModel && !state.trainingInProgress && [
        /* Edit */
        {
          key: 'edit',
          label: t('market:model.action.edit-model'),
          onClick: async () => {
            await dialogEditModel({
              model,
              mutate: props.onUpdate,
            })
          },
        },
        /* Publish */
        state.isPrivate &&
          !isLoraCategoryNotSet(model) && {
            key: 'publish',
            label: t('action:publish'),
            onClick: () => {
              publishLora(model.id)
            },
          },
        /* Delete */
        {
          key: 'delete',
          label: t('market:model.action.delete-model'),
          className: 'text-red-600 dark:text-red-500',
          onClick: async () => {
            track('delete_click')
            await deleteModel(model)
          },
        },
      ],
      ...(props.menu ?? []),
    ].flat(),
  )

  if (resolvedMenu.length === 0) return null

  return (
    <UIMenuTrigger>
      <AriaButton className="absolute top-1.5 right-1.5 flex justify-center items-center size-7 text-white bg-black/50 ring-1 ring-white/30 backdrop-blur rounded-full">
        <MingcuteMore2Fill className="size-5" />
      </AriaButton>
      <UIMenu
        placement="end"
        className={cx(
          'mt-1 overflow-hidden py-2 text-sm bg-white/80 dark:bg-black/80 backdrop-blur ring-1 ring-black/10 dark:ring-white/30 rounded-[12px] rounded-tr shadow-lg',
        )}
      >
        {resolvedMenu.map(item => (
          <UIMenuItem
            key={item.key}
            className={item.className}
            style={item.style}
            onAction={() => item.onClick?.(model)}
          >
            {item.label}
          </UIMenuItem>
        ))}
      </UIMenu>
    </UIMenuTrigger>
  )
}

function ModelCardLikeButton(props: {
  model: GenerationModelForModelCard
  scene?: string
}) {
  const scene = props.scene || 'market'

  return (
    <BaseLikeButton
      id={props.model.id}
      initialValue={props.model.liked}
      initialCount={props.model.likedCount}
      handler={async (modelId, target) => {
        await executeMarkGenerationModel({
          modelId,
          type: GenerationModelMarkType.Like,
          target,
        })
      }}
      onBtnClick={targetValue => {
        if (targetValue) {
          track('like_click', {
            scene,
            type: props.model.type,
          })
        }
      }}
      render={({ value, count, onClick }) => (
        <button
          className="ml-auto inline-flex items-center gap-1 px-2 py-1 bg-black/50 ring-1 ring-white/20 backdrop-blur rounded-[12px]"
          onClick={ev => {
            ev.preventDefault()
            ev.stopPropagation()
            onClick(ev)
          }}
        >
          <LikeHeart liked={value} className="-ml-px size-5" />
          <span className="text-sm tabular-nums text-shadow-none">
            {formatNum(count)}
          </span>
        </button>
      )}
    />
  )
}
